import DOMPurify from 'dompurify';

const RICH_TEXT_ALLOWED_TAGS = [
    'p',
    'br',
    'strong',
    'b',
    'em',
    'i',
    'u',
    'a',
    'ul',
    'ol',
    'li',
    'blockquote',
] as const;

const RICH_TEXT_SANITIZE_OPTIONS = {
    ALLOWED_TAGS: [...RICH_TEXT_ALLOWED_TAGS],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
};

function sanitizeToString(
    dirty: string,
    config?: Parameters<typeof DOMPurify.sanitize>[1],
): string {
    return String(DOMPurify.sanitize(dirty, config));
}

function withSafeExternalLinks(html: string): string {
    return html.replace(
        /<a\s+/gi,
        '<a target="_blank" rel="noopener noreferrer" ',
    );
}

function isEffectivelyEmptyRichText(html: string): boolean {
    const plainText = sanitizeToString(html, {
        ALLOWED_TAGS: [],
    }).trim();
    return !plainText;
}

export function sanitizeRichTextHtml(html: string | null | undefined): string {
    if (typeof html !== 'string' || !html.trim()) {
        return '';
    }

    const sanitized = sanitizeToString(html, RICH_TEXT_SANITIZE_OPTIONS);
    if (isEffectivelyEmptyRichText(sanitized)) {
        return '';
    }

    return withSafeExternalLinks(sanitized);
}

/** Layout Info blocks (TextArea HTML) and similar untrusted markup. */
export function sanitizeLayoutInfoHtml(html: string | null | undefined): string {
    return sanitizeRichTextHtml(html);
}

const LEADING_PARAGRAPH_WHITESPACE = /^[\s\u00a0]+/;

export function stripLeadingWhitespaceFromPlainParagraph(text: string): string {
    return text.replace(LEADING_PARAGRAPH_WHITESPACE, '');
}

/** Removes manual leading spaces at the start of each `<p>` (FR-05, about section). */
export function stripLeadingWhitespaceFromRichTextParagraphs(
    html: string | null | undefined,
): string {
    const sanitized = sanitizeRichTextHtml(html);
    if (!sanitized) {
        return '';
    }

    if (typeof document === 'undefined') {
        return sanitized.replace(
            /<p(\s[^>]*)?>(?:\s|&nbsp;|\u00a0)+/gi,
            '<p$1>',
        );
    }

    const root = document.createElement('div');
    root.innerHTML = sanitized;

    root.querySelectorAll('p').forEach((paragraph) => {
        const walker = document.createTreeWalker(
            paragraph,
            NodeFilter.SHOW_TEXT,
        );
        let textNode = walker.nextNode();

        while (textNode) {
            const current = textNode.textContent ?? '';
            const stripped = stripLeadingWhitespaceFromPlainParagraph(current);
            if (stripped !== current) {
                textNode.textContent = stripped;
                break;
            }
            if (stripped.length > 0) {
                break;
            }
            textNode = walker.nextNode();
        }
    });

    return root.innerHTML;
}

export function stripRichTextHtmlToPlainText(
    html: string | null | undefined,
): string {
    if (typeof html !== 'string' || !html.trim()) {
        return '';
    }

    return sanitizeToString(html, { ALLOWED_TAGS: [] })
        .replace(/\s+/g, ' ')
        .trim();
}

/** Aligns with Quill `editor.getText().replace(/\n$/, '')` for length limits. */
export function getRichTextPlainTextLength(
    html: string | null | undefined,
): number {
    if (typeof html !== 'string' || !html.trim()) {
        return 0;
    }

    if (typeof document === 'undefined') {
        return stripRichTextHtmlToPlainText(html).length;
    }

    const root = document.createElement('div');
    root.innerHTML = sanitizeRichTextHtml(html);
    return (root.textContent ?? '').replace(/\n$/, '').length;
}
